import { reducerWithInitialState } from "typescript-fsa-reducers";

import { CompanyMembership, CompanyMembershipSection } from "../entities";
import { DenormalizedSection } from "../../company_memberships/entities";
import {
  bootstrapCompanyMembers,
  loadCompanyMembers,
  bootstrapCompanyShow,
  loadCompanyShow,
  draftMembershipsChanged,
  saveDraftCompanyMemberships,
  draftReset,
} from "../actions";
import { CompanyMembersResponse, CompanyShowResponse } from "../api";
import { denormalizedSectionSelector, featuredMembershipsSelector } from "../../company_memberships/selectors";
import { EntityValidationResultMap, EntityValidationMapError } from "../../lib/entities/validation";

export interface MembersState {
  membershipIds: EntityId[];
  sectionIds: EntityId[];
  isLoaded: boolean;
  draftMembershipSections: DenormalizedSection[];
  draftFeaturedMemberships: CompanyMembership[];
  draftMembershipSectionsErrors: EntityValidationResultMap<CompanyMembershipSection>;
  isDraftMembershipsDirty: boolean;
}

const InitialState: MembersState = {
  sectionIds: [],
  membershipIds: [],
  isLoaded: false,
  draftMembershipSections: [],
  draftFeaturedMemberships: [],
  draftMembershipSectionsErrors: {},
  isDraftMembershipsDirty: false,
};

const retrieveStates = (state: MembersState, { result, entities }: CompanyMembersResponse) => {
  return {
    ...state,
    sectionIds: result.sections,
    membershipIds: result.memberships,
    isLoaded: true,
    draftMembershipSections: denormalizedSectionSelector(entities, result.memberships, result.sections),
    draftFeaturedMemberships: featuredMembershipsSelector(entities, result.memberships),
    draftMembershipSectionsErrors: {},
    isDraftMembershipsDirty: false,
  };
};

function initStateFromBootstrapShow(state: MembersState, { result, entities }: CompanyShowResponse) {
  return {
    ...state,
    draftFeaturedMemberships: featuredMembershipsSelector(entities, result.featured_memberships),
  };
}

const membersReducer = reducerWithInitialState<MembersState>(InitialState)
  .case(bootstrapCompanyMembers, (state, response) => retrieveStates(state, response))
  .case(loadCompanyMembers.done, (state, { result: actionRes }) => retrieveStates(state, actionRes))
  .case(bootstrapCompanyShow, initStateFromBootstrapShow)
  .case(loadCompanyShow.done, (state, { result: actionRes }) => initStateFromBootstrapShow(state, actionRes))
  .case(draftReset, (state, entities) => {
    const draftMembershipSections = denormalizedSectionSelector(entities, state.membershipIds, state.sectionIds);
    const draftFeaturedMemberships = featuredMembershipsSelector(entities, state.membershipIds);
    return {
      ...state,
      draftMembershipSections,
      draftFeaturedMemberships,
      draftMembershipSectionsErrors: {},
      isDraftMembershipsDirty: false,
    };
  })
  .case(draftMembershipsChanged, (state, result) => {
    return {
      ...state,
      draftMembershipSections: result.membershipSections,
      draftFeaturedMemberships: result.featuredMemberships,
      isDraftMembershipsDirty: true,
    };
  })
  .case(saveDraftCompanyMemberships.done, (state, { result }) => {
    return {
      ...state,
      sectionIds: result.sectionIds,
      draftMembershipSections: denormalizedSectionSelector(result.entities, state.membershipIds, result.sectionIds),
      draftFeaturedMemberships: featuredMembershipsSelector(result.entities, state.membershipIds),
      draftMembershipSectionsErrors: {},
      isDraftMembershipsDirty: false,
    };
  })
  .case(saveDraftCompanyMemberships.failed, (state, { error }) => {
    if (error instanceof EntityValidationMapError) {
      return { ...state, draftMembershipSectionsErrors: error.getResult() };
    }
    return state;
  });

export default membersReducer;



// WEBPACK FOOTER //
// ./frontend/src/companies/reducers/members.ts